import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabButton from "../../../../../../../Components/common/TabButton/TabButton";
import NotificationHeader from "../../NotificationHeader";
import Field from "../../../../Components/Field";
import Tooltip from "../../../../../../../Components/common/Tooltip/Tooltip";
import Spinner from "../../../../../../../Components/common/Spinner/Spinner";
import Backdrop from "../../../../../../../Components/UI/Backdrop/Backdrop";

import {
  RequestGetPrtgConfigs,
  RequestSetPrtgConfigs,
} from "../../../../../../../store/actions/settingsActions";
import { addNotification } from "../../../../../../../store/actions/notifierActions";

import "./styles.scss";

const isValidIpOrDomain = (trimmedInput) => {
  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

  const hostnameRegex = /^(?!\d{1,3}(\.\d{1,3}){1,3}$)([a-zA-Z0-9-]{1,63})(\.[a-zA-Z0-9-]{1,63})*$/;

  return ipv4Regex.test(trimmedInput) || hostnameRegex.test(trimmedInput);
};

const isValidPort = (trimmedInput) => {
  const port = Number(trimmedInput);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

const PrtgNotification = ({ selectedNotification }) => {
  const dispatch = useDispatch();

  const [configs, setConfigs] = useState({
    ip: "",
    port: "5050",
    token: "",
    https: 0,
    active: false,
  });

  const configsFromDB = useSelector((state) => state.settings.prtgConfigs);
  const getConfigsLoading = useSelector((state) => state.settings.prtgConfigsLoading);
  const setConfigsLoading = useSelector((state) => state.settings.setPrtgConfigsLoading);

  const configsFromDBRef = useRef(configsFromDB);

  const isPrtgActive = !!configs.active;

  useEffect(() => {
    dispatch(RequestGetPrtgConfigs());
  }, [dispatch]);

  useEffect(() => {
    if (configsFromDB) {
      configsFromDBRef.current = configsFromDB;
      const { ip, port, token, https, active } = configsFromDB;
      setConfigs({
        ip: ip || "",
        port: port || "5050",
        token: token || "",
        https: https || false,
        active: active || false,
      });
    }
  }, [configsFromDB]);

  // De-activate prtg if ip or token is empty
  useEffect(() => {
    return () => {
      const { ip, token, active } = configsFromDBRef.current;
      if (active && (!ip?.trim() || !token?.trim())) {
        dispatch(RequestSetPrtgConfigs({ active: false }));
      }
    };
  }, [dispatch]);

  const toggleActiveStateHandler = () => {
    if (getConfigsLoading || setConfigsLoading) return;
    dispatch(RequestSetPrtgConfigs({ active: !isPrtgActive }));
  };

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (name === "https") value = value === "true" ? true : false;

    setConfigs((prevConfigs) => ({ ...prevConfigs, [name]: value }));
  };

  const checkSaveButtonDisabled = () => {
    const keys = Object.keys(configs);
    for (const key of keys) {
      if (key === "active") continue;
      else if (key === "https" && configs[key] === configsFromDB[key]) continue;
      else if (key === "https" && configs[key] !== configsFromDB[key]) return false;
      else if (configs[key]?.trim() === configsFromDB[key]) continue;
      else if (configs[key] !== configsFromDB[key]) return false;
    }
    return true;
  };

  const saveHandler = () => {
    if (getConfigsLoading || setConfigsLoading) return;

    const trimmedIpOrDomain = configs.ip.trim();
    const trimmedPort = configs.port.trim();
    const trimmedToken = configs.token.trim();

    if (!isValidIpOrDomain(trimmedIpOrDomain)) {
      dispatch(
        addNotification({
          message: "Enter a valid ip/domain",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if (!isValidPort(trimmedPort)) {
      dispatch(
        addNotification({
          message: "Enter a valid port",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if (trimmedToken.length === 0) {
      dispatch(
        addNotification({
          message: "Enter a sensor token",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    dispatch(
      RequestSetPrtgConfigs({
        ip: trimmedIpOrDomain,
        port: trimmedPort,
        token: trimmedToken,
        https: configs.https,
      }),
    );
  };

  return (
    <>
      <div className="button-block">
        <TabButton title="Prtg" onClick={() => {}} active="Prtg" />
      </div>
      <NotificationHeader
        selectedNotification={selectedNotification}
        note="Configure prtg settings below"
        toggleProps={{
          show: true,
          text: "Status",
          active: isPrtgActive,
          onClick: toggleActiveStateHandler,
        }}
      />
      <div className="prtg__body__content">
        <div className="prtg__line">
          <div className="p-r-20">
            <Field
              label="IP / Domain"
              tooltipText="• Must be a valid IPv4 address (e.g., 192.168.1.10), or
• A valid hostname or domain (e.g., prtg.local or prtg.company.com)
• Each part (label) must be 1–63 characters, using only letters, numbers, or dashes
• Cannot start or end with a dash (-)
• No spaces or special characters allowed
• Do not include protocol (e.g., http://) or port (e.g., :5050)"
              width="188px"
              name="ip"
              value={configs.ip}
              onChange={inputChangeHandler}
              placeholder="192.168.196.x"
            />
          </div>
          <Field
            label="Port"
            tooltipText="• Must be a number between 1 and 65535.
• No letters, symbols, or spaces allowed.
• Leading and trailing spaces will be ignored."
            width="92px"
            name="port"
            value={configs.port}
            onChange={inputChangeHandler}
            placeholder="5050"
          />
        </div>

        <div className="prtg__line">
          <Field
            label="Sensor Token"
            width="318px"
            name="token"
            value={configs.token}
            onChange={inputChangeHandler}
            placeholder="Enter sensor token"
          />
        </div>

        <div className="prtg__line">
          <div className="prtg__radio">
            <p className="prtg__radio__header">
              Https
              <Tooltip
                text={`If yes, https protocol will be used.
If no, http protocol will be used.`}
              />
            </p>
            <div className="prtg__radio__labels">
              <label>
                <input
                  className="prtg__radio__input"
                  type="radio"
                  value={true}
                  name="https"
                  checked={configs.https}
                  onChange={inputChangeHandler}
                />
                <p>YES</p>
              </label>
              <label>
                <input
                  className="prtg__radio__input"
                  type="radio"
                  value={false}
                  name="https"
                  checked={!configs.https}
                  onChange={inputChangeHandler}
                />
                <p>NO</p>
              </label>
            </div>
          </div>
        </div>

        <button
          className="primary-button"
          onClick={saveHandler}
          disabled={checkSaveButtonDisabled()}>
          {getConfigsLoading || setConfigsLoading ? <Spinner width={12} height={12} /> : "Save"}
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!isPrtgActive}
          message="Prtg is inactive! Please click on the status button to activate prtg."
          messageStyle={{ paddingTop: 0 }}
        />
      </div>
    </>
  );
};

export default PrtgNotification;
